import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FeesService } from '../../services/fees.service';

/** The mockup's fee "Sub Type" (fees.fee_category). */
const CATEGORIES = ['Inspection', 'Reinspection', 'Additional Fee', 'Discount'];

/** Transaction sub types a discount fee posts when the inspection is completed (fees.sub_type). */
const DISCOUNT_TYPES = [
  { name: 'Inspection Discount - Prepay', value: 'Inspection Discount - Prepay' },
  { name: 'Inspection Discount - Package', value: 'Inspection Discount - Package' },
  { name: 'Inspection Discount - Inspector', value: 'Inspection Discount - Inspector' },
  { name: 'Inspection Discount - Customer Satisfaction', value: 'Inspection Discount - Customer Satisfaction' },
  { name: 'Completion Discount - Inspector', value: 'Completion Discount - Inspector' },
  { name: 'Completion Discount - Crew Manager', value: 'Completion Discount - Crew Manager' },
  { name: 'Completion Discount - Executive', value: 'Completion Discount - Executive' },
  { name: 'Completion Discount - Customer Satisfaction', value: 'Completion Discount - Customer Satisfaction' },
  { name: 'Completion - Inspector Miss', value: 'Completion - Inspector Miss' },
  { name: 'Completion - Crew Miss', value: 'Completion - Crew Miss' },
  { name: 'Inspection Adjustment - Accounting', value: 'Inspection Adjustment - Accounting' },
  { name: 'Completion Adjustment - Accounting', value: 'Adjustment - Accounting' },
  { name: 'Inspection Discount - Marketing', value: 'Inspection Discount - Marketing' },
];

/**
 * Settings > Office Fees (mockup settings "Office Fees"): the fees in their drag order (drag a row
 * by its handle - shown on hover - to set the order Scheduling lists them in), add and edit in a
 * dialog. Category is the mockup's Sub Type; a discount also keeps its Discount Type (what it posts
 * to accounting). No foundation = any foundation; "Calculate per sq ft" charges the amount per
 * square foot of the property. /office-fees/add and /office-fees/edit/:id open the dialog.
 */
@Component({
  selector: 'app-fees-list',
  templateUrl: './fees-list.component.html',
  styleUrls: ['./fees-list.component.scss']
})
export class FeesListComponent implements OnInit {
  readonly categories = CATEGORIES;
  readonly discountTypes = DISCOUNT_TYPES;

  fees: any[] = [];
  branches: any[] = [];
  branchNames: { [id: number]: string } = {};
  loaded = false;
  message = '';
  error = '';

  editing: any = null;   // the dialog's fee; fee_id '' = new
  saving = false;
  formError = '';
  fieldErrors: { [field: string]: string } = {};
  deleting: any = null;

  constructor(private feesService: FeesService, private route: ActivatedRoute, private router: Router) { }

  ngOnInit() {
    this.feesService.getBranchList().subscribe(data => {
      this.branches = (data && data.result && data.result.branches) || [];
      this.branches.forEach(b => this.branchNames[b.branch_id] = b.branch_name);
      this.load();
    });
  }

  private load() {
    this.feesService.getAllFees().subscribe(
      data => {
        this.fees = (data && data.result && data.result.data) || [];
        this.loaded = true;
        this.openFromUrl();
      },
      err => this.error = typeof err === 'string' ? err : 'The fees could not be loaded.'
    );
  }

  private openFromUrl() {
    const url = this.router.url;
    const id = this.route.snapshot.params.feeId;
    if (id) {
      const fee = this.fees.filter(f => f.fee_id == id)[0];
      if (fee) {
        this.edit(fee);
      } else {
        this.error = 'That fee no longer exists.';
      }
    } else if (/\/office-fees\/add$/.test(url)) {
      this.add();
    }
  }

  // ---- List ----

  range(f: any): string {
    return (+f.square_min === 0 && +f.square_max === 0) || f.square_min === '' ? '' : (+f.square_min).toLocaleString() + '-' + (+f.square_max).toLocaleString();
  }

  foundation(f: any): string {
    return f.foundation_type === 'slab' ? 'Slab' : (f.foundation_type === 'raised' ? 'Raised' : 'Any');
  }

  amount(f: any): string {
    const value = '$' + Math.abs(+f.fee_amount).toFixed(2);
    return (+f.fee_amount < 0 ? '-' + value : value) + (f.is_calculate_per_sq == 1 ? ' / sq ft' : '');
  }

  onDrop() {
    const order = this.fees.map((fee, index) => ({ fee_id: fee.fee_id, fee_order: index + 1 }));
    this.feesService.feeOrder(order).subscribe(
      () => this.message = 'Fee order saved.',
      err => this.error = typeof err === 'string' ? err : 'The fee order could not be saved.'
    );
  }

  // ---- Dialog ----

  add() {
    this.open({
      fee_id: '', fee_name: '', fee_amount: '', branch_id: '', square_min: '', square_max: '', foundation_type: '',
      fee_category: '', sub_type: '', is_calculate_per_sq: false, fee_description: '',
    });
  }

  edit(f: any) {
    this.open({
      fee_id: f.fee_id, fee_name: f.fee_name, fee_amount: f.fee_amount, branch_id: f.branch_id || '',
      square_min: +f.square_min || '', square_max: +f.square_max || '', foundation_type: f.foundation_type || '',
      fee_category: f.fee_category || '', sub_type: f.sub_type || '', is_calculate_per_sq: f.is_calculate_per_sq == 1,
      fee_description: f.fee_description || '',
    });
  }

  private open(fee: any) {
    this.editing = fee;
    this.formError = '';
    this.fieldErrors = {};
    this.message = '';
  }

  close() {
    this.editing = null;
    if (this.route.snapshot.params.feeId || /\/office-fees\/add$/.test(this.router.url)) {
      this.router.navigate(['/office-fees']);
    }
  }

  save() {
    const f = this.editing;
    const e: { [field: string]: string } = {};
    if (!String(f.fee_name).trim()) { e.fee_name = 'Enter the fee name.'; }
    if (f.fee_amount === '' || f.fee_amount === null || isNaN(+f.fee_amount)) { e.fee_amount = 'Enter the amount.'; }
    if (!f.branch_id) { e.branch_id = 'Select a branch.'; }
    if (!f.fee_category) { e.fee_category = 'Select the sub type.'; }
    if (f.square_min !== '' && f.square_max !== '' && +f.square_max < +f.square_min) { e.square_max = '"To" must be at least "From".'; }
    this.fieldErrors = e;
    if (Object.keys(e).length) {
      return;
    }
    const body = Object.assign({}, f, {
      fee_amount: +f.fee_amount,
      square_min: f.square_min === '' ? '' : +f.square_min,
      square_max: f.square_max === '' ? '' : +f.square_max,
      sub_type: f.fee_category === 'Discount' ? f.sub_type : '',
      is_calculate_per_sq: f.is_calculate_per_sq ? 1 : 0,
    });
    this.saving = true;
    this.formError = '';
    this.feesService.addFee(body).subscribe(
      () => {
        this.saving = false;
        this.message = f.fee_id ? 'Fee updated.' : 'Fee added.';
        this.close();
        this.load();
      },
      err => {
        this.saving = false;
        this.formError = typeof err === 'string' ? err : 'The fee could not be saved.';
      }
    );
  }

  confirmDelete(f: any) {
    this.deleting = f;
    this.message = '';
  }

  remove() {
    this.saving = true;
    this.feesService.deleteFee(this.deleting.fee_id).subscribe(
      () => {
        this.saving = false;
        this.deleting = null;
        this.message = 'Fee deleted.';
        this.load();
      },
      err => {
        this.saving = false;
        this.deleting = null;
        this.error = typeof err === 'string' ? err : 'The fee could not be deleted.';
      }
    );
  }
}
